import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import NoDataFound from '../../../assets/images/no-data-found.png';
// import NoDataSVG from '../../../assets/dynamic-colors/No-DataSVG';
import { ReactComponent as NoDataSVG } from '../../../../assets/icons/charger-icon.svg';
import { ReactComponent as GridList } from '../../../../assets/icons/grid-list.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/plus.svg';
import { ReactComponent as TableList } from '../../../../assets/icons/table-list.svg';
import { useAppBranding } from '../../../../config/branding';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import MyButton from '../../../../shared-components/button/Button';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import TabMenus from '../../../../shared-components/tab-menus/TabMenus';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import { permissionCheck } from '../../../../shared-functions/JWTDocodeFunction';
import imagesApi from '../../../../utils/api/images';
import stationManagement from '../../../../utils/api/station-management';
import AddDevice from '../../../charger-pages/charger-management/components/AddDevice';

import DeviceManagementStation from './DeviceManagementStation';

interface Device {
  _id: string;
  chargingPointSn: string;
  chargingPointName: string;
  isDeviceOffline: boolean;
  connectorType: string;
  connectors: {
    connectorId: number;
    status: string;
    connectorType: string[];
  }[];
}

const tabMenus = ['all', 'online', 'offline'];

/**
 * Devices
 * @returns {any} The rendered component
 */
function Devices() {
  const dispatch = useAppDispatch();
  const { images: brandImage } = useAppBranding();
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const [openTab, setOpenTab] = useState<number>(0);
  const [devicesData, setDevicesData] = useState<Device[]>([]);
  const [deviceImages, setDeviceImages] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [addDeviceOpen, setAddDeviceOpen] = useState(false);
  const [viewDeviceOpen, setViewDeviceOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<number>(0);
  const { t, i18n } = useTranslation('sidePanel');
  const viewMenus = [
    {
      name: '',
      icon: <TableList />,
      activeIcon: <TableList />,
    },
    {
      name: '',
      icon: <GridList />,
      activeIcon: <GridList />,
    },
  ];
  /**
   * Handle the tab change function
   * @param {number} number - The tab number
   */
  const changeTabPages = (number: number) => {
    setOpenTab(number);
  };
  /**
   * Fetch signed device images for the given devices
   * @param {Device[]} devices - The devices to fetch images for
   */
  const handleFetchDeviceImages = async (devices: Device[]) => {
    try {
      const results = await Promise.all(
        devices.map(async (device) => {
          try {
            const response = await imagesApi.getDeviceImage(device.chargingPointSn);
            const imageDetail = response?.data?.imageUrls?.[0];
            return { id: device._id, url: imageDetail?.signedUrl || imageDetail?.url || '' };
          } catch (error) {
            console.log(error);
            return { id: device._id, url: '' };
          }
        }),
      );
      setDeviceImages(
        results.reduce((accumulator: Record<string, string>, result) => {
          accumulator[result.id] = result.url;
          return accumulator;
        }, {}),
      );
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * device list api call
   */
  const handleDeviceList = async () => {
    setLoading(true);
    if (!selectedChargingPoint?.id) return;
    try {
      const response = await stationManagement.fetchDeviceList({
        chargingStation: selectedChargingPoint?.id,
      });
      const devices = response.data || [];
      setDevicesData(devices);
      if (devices.length <= 4) {
        handleFetchDeviceImages(devices);
      } else {
        setDeviceImages({});
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('errorMessage'),
          },
        },
      });
    }
  };

  const tabCounts = [
    devicesData.length,
    devicesData.filter((device) => !device.isDeviceOffline).length,
    devicesData.filter((device) => device.isDeviceOffline).length,
  ];

  const filteredDevices =
    openTab === 0
      ? devicesData
      : devicesData.filter((device) => {
          if (openTab === 1) return !device.isDeviceOffline; // Online
          if (openTab === 2) return device.isDeviceOffline; // Offline
          return false; // Maintenance (no field in data for now)
        });

  const statusClassMap: Record<string, string> = {
    online: 'online',
    offline: 'offline',
  };
  const showImages = filteredDevices.length <= 4;

  useEffect(() => {
    handleDeviceList();
  }, [selectedChargingPoint]);
  return (
    <div className="all-devices-container" dir={i18n.dir()}>
      {viewMode === 0 ? (
        <div className="devices-partner-table-container">
          <DeviceManagementStation
            rightTopContent={
              <TabMenus openTab={viewMode} setOpenTab={setViewMode} menus={viewMenus} />
            }
          />
        </div>
      ) : (
        <>
          <div className="devices-heading">
            <div className="table-tab">
              <CustomToggleButton
                defaultActiveIndex={openTab}
                onToggle={changeTabPages}
                labels={tabMenus.map((menu, index) => (
                  <div className="tab-label-wrapper" key={menu}>
                    <span>{t(menu)}</span>
                    <span className="tab-badge">{tabCounts[index]}</span>
                  </div>
                ))}
              />
            </div>
            <TabMenus openTab={viewMode} setOpenTab={setViewMode} menus={viewMenus} />
          </div>
          <div className="devices-content">
            <div className="online-offline">
              <p>{t('devices')}</p>
              <div className="devices-status-actions">
                <p>{t('statusDevices', { status: t(tabMenus[openTab]) })}</p>
                <MyButton
                  onClickFunc={() => setAddDeviceOpen(true)}
                  label={t('addCharger')}
                  buttonType="primary-outline"
                  svgIcon={<PlusIcon />}
                  disableClass="add-device-btn-custom"
                  disableButton={
                    !permissionCheck('partnerChargingDevicesAdd') &&
                    !permissionCheck('cpoChargingPointAdd')
                  }
                />
              </div>
            </div>

            <div>
              {(() => {
                let deviceslist = null;
                if (loading) {
                  deviceslist = (
                    <div className="skeleton-grid-loader">
                      {[...Array(10)].map((_, index) => (
                        <div className="skeleton-card" key={index} />
                      ))}
                    </div>
                  );
                } else if (filteredDevices.length > 0) {
                  deviceslist = (
                    <div className={`device-grid ${showImages ? 'images-show' : ''}`}>
                      {' '}
                      {filteredDevices.map((device) => (
                        <div
                          className="device-card"
                          key={device._id}
                          role="presentation"
                          onClick={() => {
                            setSelectedDevice(device._id);
                            setViewDeviceOpen(true);
                          }}>
                          <div
                            className={`device-status ${
                              statusClassMap[device.isDeviceOffline ? 'offline' : 'online']
                            }`}>
                            <div className="status-dot" />
                            <div className="status-name">
                              {device.isDeviceOffline ? t('offline') : t('online')}
                            </div>
                          </div>

                          {showImages && (
                            <div className="empty-charger-image">
                              <img
                                src={deviceImages[device._id] || brandImage.emptyChargerImage}
                                alt={
                                  deviceImages[device._id]
                                    ? device.chargingPointName
                                    : t('emptyCharger')
                                }
                                className="device-details-image"
                              />
                              <div
                                className={`device-status-container ${
                                  statusClassMap[device.isDeviceOffline ? 'offline' : 'online']
                                }`}>
                                <div className="static-container" />
                                <div className="animation-container" />
                              </div>
                            </div>
                          )}

                          <div className="device-info">
                            <div className="tooltip-container name">
                              <p className="device-name">{device.chargingPointName}</p>
                              <span className="tooltip-text">{device.chargingPointName}</span>
                            </div>
                            <div className="tooltip-container device-id">
                              <p className="device-name">{device.chargingPointSn}</p>
                              <span className="tooltip-text">{device.chargingPointSn}</span>
                            </div>
                          </div>

                          <div className="device-footer">
                            <div className="device-ccs">
                              {device?.connectors?.[0]?.connectorType?.[0]}
                            </div>{' '}
                            <div className="device-version">{device.connectors.length}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                } else if (filteredDevices.length === 0) {
                  deviceslist = (
                    <div className="table-no-data-wrap">
                      <div className="no-data-svg-wrapper">
                        <NoDataSVG className="devices-no-data-svg" />
                      </div>
                      <div className="no-data-found-details">
                        <h3>
                          {openTab === 0
                            ? t('noChargersFoundTitle')
                            : t('noDataFoundStatusTitle', { status: t(tabMenus[openTab]) })}
                        </h3>
                        <p>
                          {openTab === 0
                            ? t('noChargersFoundDesc')
                            : t('noDataFoundStatusDesc', {
                                status: t(tabMenus[openTab]).toLowerCase(),
                              })}
                        </p>
                      </div>
                    </div>
                  );
                }

                return deviceslist;
              })()}
            </div>
          </div>
        </>
      )}

      <SidePaneModel
        open={addDeviceOpen}
        closePanel={() => setAddDeviceOpen(false)}
        content={<AddDevice setSidePanelOpen={setAddDeviceOpen} hideHeader />}
        position="right"
        title="addDevice"
      />
      <SidePaneModel
        open={viewDeviceOpen}
        closePanel={() => {
          setViewDeviceOpen(false);
          setSelectedDevice(null);
        }}
        content={
          <DeviceSidePanel
            fetchIdFrom="props"
            id={selectedDevice || ''}
            onClose={() => {
              setViewDeviceOpen(false);
              setSelectedDevice(null);
            }}
          />
        }
        position="right"
        hideHeader
      />
    </div>
  );
}

export default Devices;
